<template>
    <label
      v-if="label"
      :for="id"
      :class="[
        'block text-sm font-semibold dark:text-white',
        !isError ? 'text-gray-800' : 'text-red-500'
      ]"
      >{{ label }} <span class="text-red-500">{{ props.required ? '*':'' }}</span></label
    >
    <div class="relative">
      <select
        v-bind="$attrs"
        :id="id"
        v-model="model"
        :disabled="readonly || Boolean($attrs.disabled)"
        :class="[
          /* agregar appearance-none para ocultar la flecha nativa */
          'form-select appearance-none -webkit-appearance-none -moz-appearance-none',
          sizes[size],
          colors[color],
          typesInput[typeInput],
          isError ? 'border-red-500 ring-red-500' : ''
        ]"
        v-on="eventHandlers"
      >
        <option v-if="placeholder" disabled selected>{{ placeholder }}</option>
        <slot
          name="options"
          v-bind="{
            optionsDefault: dynamicOptions || optionsDefault,
            optionsDefaultGroup,
            mapOptions,
            optionText,
            optionValue
          }"
        >
          <template v-if="basicSelect">
            <option
              v-for="item of dynamicOptions || optionsDefault"
              :key="(item as string | number)"
              :value="item"
            >
              {{ item }}
            </option>
          </template>
          <template v-else-if="optionsDefaultGroup">
            <optgroup
              v-for="(optionsDefaultGroupItem, nombre) in optionsDefaultGroup"
              :key="nombre"
              :label="nombre"
            >
              <option
                v-for="item of (optionsDefaultGroupItem as object)"
                :key="mapOptions ? mapOptions(item).optionValue : optionValue && (item)[optionValue]"
                :value="mapOptions ? mapOptions(item).optionValue : optionValue && (item)[optionValue]"
              >
                {{
                  mapOptions
                    ? mapOptions(item).optionText
                    : optionText && (item)[optionText]
                }}
              </option>
            </optgroup>
          </template>
          <template v-else>
            <option
              v-for="item of dynamicOptions || optionsDefault"
              :key="mapOptions ? mapOptions(item as object).optionValue : optionValue && (item as any)[optionValue]"
              :value="mapOptions ? mapOptions(item as object).optionValue : optionValue && (item as any)[optionValue]"
              :disabled="mapOptions ? mapOptions(item as object).disabled : (item as any).disabled || false"
            >
              {{
                mapOptions
                  ? mapOptions(item as object).optionText
                  : optionText && (item as object)[optionText]
              }}
            </option>
          </template>
        </slot>
      </select>
      <button
        v-if="clear && model && !disabled && !$attrs.disabled && !readonly"
        type="button"
        aria-label="Limpiar selección"
        class="cursor-pointer absolute shrink-0 size-6 flex items-center justify-center rounded-md border border-transparent text-blue-600 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:pointer-events-none"
        :style="sizesButtonClear[size]"
        @click="clearChange"
      >
        <XMarkIcon class="size-4" />
      </button>
      <div
        v-if="!disabled && !$attrs.disabled && !readonly"
        class="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 size-6 flex items-center justify-center rounded-md border border-transparent"
        :class="readonly || disabled || $attrs.disabled ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600'"
      >
        <ChevronDownIcon class="size-4" />
      </div>
      <IconErrorTag :is-error="isError" />
    </div>
    <ErrorTag
      :is-error="isError"
      :error-message="errorMessage"
    />
</template>

<script setup lang="ts">
import { onMounted, ref, computed, toRefs } from 'vue'
import { XMarkIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import type { ModelValue } from '@/modules/Core/usings/constantes'
import * as services from '@/services'
import type { ISelectOption, ServicesType } from '@/modules/Core/interfaces'
import { getEventHandlers, getIsError, generarUuid } from '@/modules/Core/usings/generales'
import ErrorTag from './ErrorTag.vue'
import type { ErrorObject } from '@vuelidate/core'
import type { IConfCompxfuncion } from '@/modules/Administracion/interfaces'
const model = defineModel<ModelValue>()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const emit = defineEmits(['cambiarValor'])
const props = defineProps<{
  id?: string
  label?: string
  options?: unknown[]
  optionsGroup?: object
  optionValue?: string
  optionText?: string
  disabled?: boolean
  mapOptions?: (item: object) => ISelectOption
  urlSearch?: keyof ServicesType
  basicSelect?: boolean
  clear?: boolean
  clearEvent?: () => void
  color?: string
  typeInput?: string
  size?: string
  error?: string | Array<ErrorObject> | null | undefined
  required?: boolean
  readonly?: boolean
  params?: string | Record<string, string>
  action?: string
  placeholder?: string
  compxfunciones?: IConfCompxfuncion[]
}>()

const {
  id = generarUuid(),
  readonly = false,
  disabled = false
} = toRefs(props)
const errorMessage = computed(()=> props.error)
const eventHandlers = computed(() => {
  let handlers: Record<string, (e: Event) => void> = {}

  if (props.compxfunciones && props.compxfunciones.length > 0) {
    handlers = getEventHandlers(typeof(props.compxfunciones) === 'string' ? JSON.parse(props.compxfunciones): props.compxfunciones)
  }
  return handlers
})

const isError = computed(() => {
  const isArray = Array.isArray(props.error)
  return getIsError(props, isArray)
})
const clearChange = () => {
  model.value = null
  if (props.clearEvent) props.clearEvent()
}
const {
  urlSearch,
  action,
  params,
  basicSelect = false,
  color = 'default',
  typeInput = 'default',
  size = 'default',
  optionText,
  optionValue
} = props
const sizes: Record<string, string> = {
  small: ' py-2 px-3',
  default: ' py-2 px-3',
  medium: ' py-3 px-4',
  large: ' p-4 sm:p-5'
}

const sizesButtonClear = {
  small: ' right: 3rem; top: 50%; transform: translateY(-50%); ',
  default: ' right: 2.3rem; top: 50%; transform: translateY(-50%); ',
  large: ' right: 3.25rem; top: 50%; transform: translateY(-50%); '
} as Record<string, string>

const colors = {
  default: ' focus:border-blue-500 focus:ring-blue-500 ',
  red: ' focus:border-red-500 focus:ring-red-500 ',
  blue: ' focus:border-blue-500 focus:ring-blue-500 ',
  green: ' focus:border-green-500 focus:ring-green-500 ',
  yellow: ' focus:border-yellow-500 focus:ring-yellow-500 '
} as Record<string, string>
const typesInput = {
  default:
    '  block w-full border-gray-600 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:pointer-events-none dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400 dark:focus:ring-gray-600 dark:disabled:bg-slate-800 dark:disabled:text-gray-400 ',
  grayinput:
    ' peer  ps-11 block w-full bg-gray-100 border-transparent rounded text-sm disabled:opacity-50 disabled:pointer-events-none dark:bg-gray-700 dark:border-transparent dark:text-gray-400 dark:focus:ring-gray-600 '
} as Record<string, string>
const optionsDefault = computed(() => props.options)
const optionsDefaultGroup = ref(props.optionsGroup)
const dynamicOptions = ref(null)

onMounted(async () => {
  if (urlSearch && action) {
    const { data } = await services[urlSearch as keyof ServicesType][action](
      params
    )
    dynamicOptions.value = data.body
  }
})
</script>
